import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Body2, Header2, SkeletonLoader } from "@activecollab/components";
import classNames from "classnames";
import PropTypes from "prop-types";
import { usePrepareFileUrl } from "~/components/Hooks/usePrepareFileUrl";
import { PublicInvoiceObjectAmount } from "~/components/InvoiceObject/PublicInvoiceObjectAmount";
import { NlToBr } from "~/components/Text/NlToBr";
import { formatDateSmart } from "~/utils/date.jsx";

const PublicInvoiceObjectViewHeader = ({ object, isLoading, template, dateFormat, currency }) => {
  const { t } = useTranslation();
  const logoUrl = usePrepareFileUrl(template?.logo, 160, 160, "scale");

  const invoiceName = useMemo(() => {
    if (!object) {
      return null;
    }

    const invoicePrefix = template?.label ? template.label : t("Invoice");
    return invoicePrefix + ": " + object.number;
  }, [t, object, template]);

  return (
    <div className="view-estimates-body tw-mt-7 tw-mx-7">
      <div
        className={classNames(
          "company_details tw-flex tw-justify-between tw-pb-6 tw-border-b tw-border-solid tw-border-0 tw-border-border-primary tw-mb-10 tw-items-center tw-min-h-[115px]",
          {
            "tw-border-b-gray-100": isLoading,
          }
        )}
      >
        <div
          data-testid="invoice-header"
          className={classNames("tw-flex tw-flex-1 tw-flex-col", {
            "tw-items-start tw-order-1": template?.header_layout === 1,
            "tw-items-end tw-order-2 tw-text-right": template?.header_layout === 0,
          })}
        >
          {isLoading ? (
            <>
              <SkeletonLoader />
              <SkeletonLoader className="tw-w-[150px] tw-mt-3" />
              <SkeletonLoader className="tw-w-[150px] tw-mt-3" />
              <SkeletonLoader className="tw-w-[80px] tw-mt-3" />
            </>
          ) : (
            <Body2
              color="primary"
              lineHeight="loose"
              align={template?.header_layout === 0 ? "right" : "left"}
            >
              <strong>{template?.company_name}</strong>
              <br />
              <NlToBr>{template?.company_details}</NlToBr>
            </Body2>
          )}
        </div>

        <div
          className={classNames("tw-flex tw-flex-1 tw-items-start", {
            "tw-justify-end tw-order-2": template?.header_layout === 1,
            "tw-justify-start tw-order-1": template?.header_layout === 0,
          })}
        >
          {logoUrl && (
            <img
              src={logoUrl}
              style={{ maxWidth: 180, maxHeight: 80 }}
              alt="Logo"
            />
          )}
        </div>
      </div>

      <div className="invoice_details tw-flex tw-justify-between tw-mb-10 tw-min-h-[70px]">
        <div
          data-testid="invoice-details-left"
          className={classNames("tw-max-w-[50%]", {
            "tw-order-last": template?.body_layout === 1,
          })}
        >
          {isLoading ? (
            <>
              <SkeletonLoader className="tw-w-[220px] tw-mt-3" />
              <SkeletonLoader className="tw-w-[150px] tw-mt-3" />
              <SkeletonLoader className="tw-w-[150px] tw-mt-3" />
            </>
          ) : (
            <>
              <div
                className={classNames("tw-flex tw-w-full tw-items-center", {
                  "tw-justify-end": template?.body_layout === 1,
                })}
              >
                <Body2
                  weight="bold"
                  lineHeight="loose"
                >
                  {t("For:")}&nbsp;
                </Body2>
                <Body2>{object?.company_name}</Body2>
              </div>
              <Body2
                lineHeight="loose"
                align={template?.body_layout === 1 ? "right" : "left"}
              >
                <NlToBr>{object?.company_address}</NlToBr>
              </Body2>
            </>
          )}
        </div>

        <div
          data-testid="invoice-details-right"
          className="tw-max-w-[50%]"
        >
          {isLoading ? (
            <div className="tw-flex tw-flex-col tw-items-end">
              <SkeletonLoader className="tw-w-[150px] tw-mt-3" />
              <SkeletonLoader className="tw-w-[70px] tw-mt-3" />
            </div>
          ) : (
            object && (
              <>
                <Header2
                  align={template?.body_layout === 1 ? "left" : "right"}
                  lineHeight="loose"
                >
                  {invoiceName}
                </Header2>

                {object?.purchase_order_number ? (
                  <Body2
                    lineHeight="loose"
                    align={template?.body_layout === 1 ? "left" : "right"}
                  >
                    <strong>{t("Reference")}:</strong>&nbsp;
                    {object?.purchase_order_number}
                  </Body2>
                ) : null}

                {object?.issued_on && object?.computed_status !== "canceled" ? (
                  <Body2
                    lineHeight="loose"
                    align={template?.body_layout === 1 ? "left" : "right"}
                    data-testid="invoice-issued-on"
                  >
                    <strong>{t("Issued On")}:</strong>&nbsp;
                    {formatDateSmart(object.issued_on, dateFormat, true)}
                  </Body2>
                ) : null}

                {object?.due_on && object?.computed_status !== "canceled" ? (
                  <Body2
                    lineHeight="loose"
                    align={template?.body_layout === 1 ? "left" : "right"}
                    className={classNames({
                      "tw-text-red-alert": object?.is_overdue,
                    })}
                    data-testid="invoice-due-on"
                  >
                    <strong>{t("Due On")}:</strong>&nbsp;
                    {formatDateSmart(object.due_on, dateFormat, true)}
                  </Body2>
                ) : null}

                {object?.computed_status === "canceled" ? (
                  <Body2
                    lineHeight="loose"
                    align={template?.body_layout === 1 ? "left" : "right"}
                  >
                    <strong>{t("Canceled On")}:</strong>&nbsp;
                    {formatDateSmart(object.closed_on, dateFormat, true)}
                  </Body2>
                ) : null}

                <Body2
                  lineHeight="loose"
                  align={template?.body_layout === 1 ? "left" : "right"}
                  data-testid="invoice-total"
                >
                  <strong>{t("Total")}:</strong>&nbsp;
                  <PublicInvoiceObjectAmount
                    currency={currency}
                    total={object.total}
                    trimDecimals={false}
                    format="long"
                  />
                </Body2>
              </>
            )
          )}
        </div>
      </div>
    </div>
  );
};

PublicInvoiceObjectViewHeader.displayName = "InvoiceObjectViewHeader";
PublicInvoiceObjectViewHeader.propTypes = {
  object: PropTypes.object,
  isLoading: PropTypes.bool,
  template: PropTypes.object,
  dateFormat: PropTypes.string,
  currency: PropTypes.object,
};

export default PublicInvoiceObjectViewHeader;
